import axios from "axios";
import { GEEKS_USER } from "constants/profile";
import { getDataFromLocalStorage, localUserExist } from "helpers/generalHelper";
import { toast } from "helpers/generalHelper";
import * as Sentry from "@sentry/react";

// Get token from local storage
const getToken = () => {
  return getDataFromLocalStorage(GEEKS_USER)?.token || null;
};

// Common Axios Instance Config
const axiosConfig = {
  baseURL: process.env.REACT_APP_BASE_URL, // Development
};

// Create Default Axios Instances
const instance = axios.create(axiosConfig);
const instanceWithoutInterceptors = axios.create(axiosConfig);

// Interceptor for adding common headers to requests without authorization
instanceWithoutInterceptors.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = "en"; // Set content language header
  return config;
});

// Interceptor to add Authorization token for authenticated requests
instance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`; // Set Authorization header
  }
  return config;
});

// Add interceptors for validate authorization
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { status, data } = error?.response || {};
    const url = error?.config?.url;
    const env = process.env.REACT_APP_NODE_ENV;
    const sentryEnabled = !!process.env.REACT_APP_SENTRY_DNS;
    const isProductionOrStaging = ["production", "staging"].includes(env);

    // --- Capture server error in Sentry ---
    if (sentryEnabled && status === 500 && isProductionOrStaging) {
      Sentry.captureException(error);
    }

    // --- Handle specific validation and lock errors ---
    const isSuggestionError =
      status === 422 && url === "/team-run/trade/companies/suggestions";
    const isLockError =
      status === 423 &&
      [
        "/vol-surface/strike-surfaces/control-table/update",
        "/vol-surface/delta-surfaces/control-table",
        "/vol-surface/delta-surfaces/control-table/vol-calc-table",
      ].includes(url);

    if (isSuggestionError || isLockError) {
      return error?.response;
    }

    // --- Handle general validation error ---
    if (status === 422) {
      toast(data?.message, "warning");
    }

    // --- Handle other errors unless request was cancelled ---
    if (status !== 422 && error.code !== "ERR_CANCELED") {
      toast(data?.message || "Backend Error", "error");
    }

    // --- Handle unauthorized access ---
    if (status === 401 && localUserExist()) {
      localStorage.clear();
      window.location.href = "/login";
    }

    return Promise.reject(new Error(error));
  }
);

// Export Axios with Auth Headers
export default instance;

// Export Axios Without instance
export { instanceWithoutInterceptors as axiosWI };
